<script>
import { mapGetters, mapActions } from "vuex";
export default {
  name: "Bahn1Anmeldung",
  data: () => ({
    form: {
      email: "",
      verpflegung: [],
      anreise: null,
      unterstuetzung: null
    },
    submitting: false
  }),
  computed: {
    ...mapGetters("bahn1", ["anmeldungEventId", "angemeldet"]),
    ...mapGetters("user", ["userEmail"]),
    emailValid() {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.email);
    },
    emailRules() {
      return [
        v => !!v || "Pflichtfeld",
        v =>
          /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ||
          "Bitte eine gültige E-Mail-Adresse eingeben"
      ];
    },
    verpflegungOptions() {
      return [
        "Vegetarisch",
        "Vegan",
        "Glutenfrei",
        "Laktosefrei",
        "Keine Besonderheiten"
      ];
    },
    anreiseOptions() {
      return ["PKW (Parkplatz benötigt)", "ÖPNV", "Noch offen"];
    },
    message() {
      const verpflegung = this.form.verpflegung.length
        ? this.form.verpflegung.join(", ")
        : "keine Angabe";
      const anreise = this.form.anreise || "keine Angabe";
      const unterstuetzung = this.form.unterstuetzung || "keine Angabe";
      return [
        `E-Mail: ${this.form.email}`,
        `Verpflegung: ${verpflegung}`,
        `Anreise: ${anreise}`,
        `Unterstützung vor Ort: ${unterstuetzung}`
      ].join("\n");
    }
  },
  methods: {
    ...mapActions("veranstaltungen", ["signUpForEvent"]),
    ...mapActions("bahn1", ["loadBahn1"]),
    ...mapActions("msgSnackbar", ["showMessage"]),
    onVerpflegungChange(option, selected) {
      if (selected.includes(option)) {
        if (option === "Keine Besonderheiten") {
          this.form.verpflegung = ["Keine Besonderheiten"];
        } else {
          this.form.verpflegung = selected.filter(
            o => o !== "Keine Besonderheiten"
          );
        }
      } else {
        this.form.verpflegung = selected;
      }
    },
    async submit() {
      this.submitting = true;
      try {
        await this.signUpForEvent({
          eventId: this.anmeldungEventId,
          message: this.message
        });
        await this.loadBahn1();
      } catch (err) {
        this.showMessage({
          text:
            "Bei der Anmeldung ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.",
          color: "error",
          icon: "mdi-alert"
        });
      } finally {
        this.submitting = false;
      }
    }
  },
  mounted() {
    this.form.email = this.userEmail;
  }
};
</script>
<template>
  <div class="anmeldung">
    <h2>Anmeldung</h2>
    <p>
      <b>Hinweis:</b> Alle Veranstaltungen sind fotoöffentlich. Während der
      Veranstaltungen entstehen Fotos, die wir für unsere Kommunikation nutzen.
    </p>
    <div v-if="!angemeldet">
      <div class="anmeldung-field anmeldung-field--email">
        <label class="anmeldung-label">Ihre E-Mail-Adresse</label>
        <v-text-field
          class="anmeldung-email"
          v-model="form.email"
          type="email"
          :rules="emailRules"
          hide-details="auto"
          dense
          outlined
          filled
          single-line
        />
      </div>
      <div class="anmeldung-field">
        <label class="anmeldung-label">Verpflegung</label>
        <div class="anmeldung-options">
          <v-checkbox
            v-for="option in verpflegungOptions"
            :key="option"
            :input-value="form.verpflegung"
            :value="option"
            :label="option"
            hide-details
            @change="onVerpflegungChange(option, $event)"
          />
        </div>
      </div>

      <div class="anmeldung-field">
        <label class="anmeldung-label">Wie reisen Sie an?</label>
        <v-radio-group
          v-model="form.anreise"
          row
          hide-details
          class="anmeldung-radio-group"
        >
          <v-radio
            v-for="option in anreiseOptions"
            :key="option"
            :value="option"
            :label="option"
          />
        </v-radio-group>
      </div>

      <div class="anmeldung-field">
        <label class="anmeldung-label">
          Benötigen Sie Unterstützung (z.B. barrierefreier Zugang,
          Begleitperson) vor Ort? Wir melden uns bei Ihnen.
        </label>
        <v-radio-group
          v-model="form.unterstuetzung"
          row
          hide-details
          class="anmeldung-radio-group"
        >
          <v-radio value="Ja" label="Ja" />
          <v-radio value="Nein" label="Nein" />
        </v-radio-group>
      </div>

      <v-btn
        color="primary"
        :loading="submitting"
        :disabled="!anmeldungEventId || !emailValid || submitting"
        @click="submit"
      >
        Jetzt anmelden
      </v-btn>
    </div>

    <div v-else class="anmeldung-success">
      <v-icon size="66">$mail2</v-icon>
      <p class="anmeldung-success-title">Vielen Dank für Ihre Anmeldung!</p>
      <p class="anmeldung-success-subtitle">
        Wir melden uns bei Ihnen. Bitte prüfen Sie auch Ihren
        E-Mail-Posteingang.
      </p>
    </div>
  </div>
</template>

<style scoped>
.anmeldung {
  background: #ffffff;
  border-radius: 5px;
  padding: 24px;
  margin-bottom: 80px;
}
h2 {
  font-size: 30px;
  letter-spacing: 0.6px;
  margin-bottom: 16px;
}
p {
  font-family: "GeometriaLight";
  letter-spacing: 0.32px;
  margin-bottom: 24px;
}
.anmeldung-label {
  display: block;
  font-family: "GeometriaMedium";
  font-size: 12px;
  letter-spacing: 0.6px;
  line-height: 1.2;
  margin-bottom: 4px;
}
.anmeldung-email {
  width: 50%;
}
.anmeldung-field {
  margin-bottom: 24px;
}
.anmeldung-field--email {
  margin-bottom: 16px;
}
.anmeldung-options {
  display: flex;
  flex-wrap: wrap;
  column-gap: 24px;
  row-gap: 8px;
}
.anmeldung-field ::v-deep .v-input--selection-controls {
  margin-top: 0 !important;
  padding-top: 0 !important;
}
.anmeldung-field ::v-deep .v-input--selection-controls .v-input__slot {
  margin-bottom: 0;
  height: 40px;
  padding: 8px;
}

.anmeldung-field
  ::v-deep
  .v-input--radio-group
  > .v-input__control
  > .v-input__slot {
  background-color: #ffffff !important;
  padding: 0;
  height: auto;
}
.anmeldung-field ::v-deep .v-radio {
  background-color: #f5dbe6;
  padding: 8px;
  height: 40px;
}
.anmeldung-field ::v-deep .anmeldung-radio-group .v-input--radio-group__input {
  flex-wrap: wrap;
  column-gap: 24px;
  row-gap: 8px;
}
.anmeldung-field ::v-deep .anmeldung-radio-group .v-radio {
  margin-right: 0;
}
.anmeldung-success {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px 0;
}
.anmeldung-success-title {
  font-family: "GeometriaGeometria";
  font-size: 20px;
  margin: 8px 0 4px;
}
.anmeldung-success-subtitle {
  font-family: "GeometriaLight";
  font-size: 16px;
  margin: 0;
}
@media (max-width: 599px) {
  .anmeldung-email {
    width: 100%;
  }
}
</style>
